"use client";
import { Fragment, type ReactNode, forwardRef } from "react";

import Flex from "./Flex";
import Box from "./Box";
import Typography from "./Typography";
import { cn } from "@/shared/libs/commonUtils";
import { Check } from "@phosphor-icons/react";
import { useDeviceType } from "@/shared/context/DeviceContext";

type TStepperOptions = {
  id: number;
  title: string;
  icon?: ReactNode;
}[];

interface IStepper {
  className?: string;
  options: TStepperOptions;
  currentStepId: number;
}

const Stepper = forwardRef<HTMLDivElement, IStepper>(
  ({ className, options, currentStepId }, ref) => {
    return (
      <Flex
        className={`w-full gap-0 flex-nowrap justify-between items-start hidden md:flex relative max-md:overflow-x-auto max-md:text-background dark:max-md:dark:text-foreground max-md:z-20 max-md:py-2 max-md:border-b hide-scrollbar ${
          useDeviceType() === "mobile" && "flex!"
        }   max-md:px-3`}
      >
        {options.map(({ id, title, icon }, index) => (
          <Fragment key={id}>
            <Flex
              ref={ref}
              className={cn(
                `w-2/3 md:w-1/3! flex-wrap flex-col justify-center items-center  md:items-center  gap-3 md:text-background dark:md:dark:text-foreground ${
                  currentStepId === id && "max-md:text-primary"
                }   ${className} z-1`,
                currentStepId >= id ? "opacity-100 text-success" : "opacity-30",
              )}
            >
              <Box className="max-md:hidden">
                {icon && currentStepId > index + 1 ? options[0].icon : icon}
              </Box>
              <Flex className="items-center gap-2">
                <Box
                  className={`w-4 h-4  text-background dark:text-foreground border border-gray-400 rounded-full p-3 flex justify-center items-center md:hidden ${
                    currentStepId === id
                      ? "bg-primary! text-background border-none"
                      : currentStepId >= id &&
                        "bg-success! text-background border-none"
                  }`}
                >
                  <Typography variant="caption">
                    {currentStepId > id ? <Check /> : index + 1}
                  </Typography>
                </Box>

                <Typography
                  variant="h4"
                  className="!text-sm !font-medium text-center max-md:!text-xs leading-loose max-md:whitespace-nowrap"
                >
                  {title}
                </Typography>
              </Flex>
            </Flex>
            {index < options.length - 1 ? (
              <Box
                className={cn(
                  "pt-px mt-[12px] bg-background dark:bg-foreground max-md:bg-primary h-px max-md:min-w-10 w-full! max-md:mx-2",
                  currentStepId > id
                    ? "opacity-100 max-md:bg-success!"
                    : "opacity-30",
                )}
              />
            ) : null}
          </Fragment>
        ))}
      </Flex>
    );
  },
);

Stepper.displayName = "Stepper";

export default Stepper;
